<template>
  <div>
    <b-input-group class="mb-3">
      <b-form-input
        v-model="displayDate"
        type="text"
        placeholder="DD/MM/YYYY"
        autocomplete="off"
        :state="isValidDate()"
        title="DD/MM/YYYY"
        @blur="updateDate"
      ></b-form-input>
      <input type="date" class="btn btn-warning" :max="maxDateStr" @change="onNativeDateChange" />
    </b-input-group>
    <b-input-group>
      <b-form-input
        v-model="selectedTime"
        type="text"
        placeholder="hh:mm"
        autocomplete="off"
        :state="isValidTime()"
        title="hh:mm"
        @blur="tidyTime"
      ></b-form-input>
      <input
        type="time"
        class="btn btn-warning"
        :value="selectedTime || ''"
        @change="onNativeTimeChange"
      />
    </b-input-group>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import moment from 'moment/moment';
import {
  combineLocalDateTime,
  formatTime,
  parseDisplayDate,
  parseTime,
} from '@/models/utils/datetime';

defineOptions({ name: 'DateTimeInput' });

const props = defineProps<{
  field: string | null;
  pastDatesOnly?: boolean;
}>();

const emit = defineEmits<{
  (e: 'datetimeEvent', payload: { field: string | null; value: Date }): void;
}>();

const selectedDate = ref<string | null>(null);
const displayDate = ref('');
const selectedTime = ref<string | null>(null);

const maxDateStr = computed(() =>
  (props.pastDatesOnly ?? true) ? moment(tomorrow()).format('YYYY-MM-DD') : '',
);

// Entered times are in the browser's time zone, like every other date in the app
watch(selectedDate, () => {
  const dateTime = combineLocalDateTime(selectedDate.value, selectedTime.value);
  emit('datetimeEvent', { field: props.field, value: dateTime });
});

watch(selectedTime, () => {
  // Seconds are ignored by parseTime; tidyTime shows HH:mm on blur
  const dateTime = combineLocalDateTime(selectedDate.value, selectedTime.value);
  emit('datetimeEvent', { field: props.field, value: dateTime });
});

function onNativeDateChange(e: Event) {
  const val = (e.target as HTMLInputElement).value;
  selectedDate.value = val;
  displayDate.value = val ? moment(val, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
}

function onNativeTimeChange(e: Event) {
  selectedTime.value = (e.target as HTMLInputElement).value;
}

function isValidDate(): boolean | null {
  if (!displayDate.value) return null;
  // Same strict parse as updateDate, so the field state matches the emitted value
  const date = parseDisplayDate(displayDate.value);
  return date !== null && (!maxDateStr.value || maxDateStr.value >= date);
}

function isValidTime(): boolean | null {
  return selectedTime.value ? parseTime(selectedTime.value) !== null : null;
}

function tomorrow(): Date {
  return moment(new Date()).add(1, 'days').toDate();
}

// Show a readable time as HH:mm, so it's clear how it was read; leave invalid input as typed
function tidyTime() {
  const tidy = formatTime(selectedTime.value);
  if (tidy && tidy !== selectedTime.value) selectedTime.value = tidy;
}

function updateDate(ctx: Event) {
  const value = (ctx.target as HTMLInputElement).value.trim();
  // Strict, so 14/06/26 or 31/02/2026 stays invalid instead of becoming another date
  selectedDate.value = value ? (parseDisplayDate(value) ?? 'invalid') : null;
}
</script>

<style scoped>
.sr-only {
  display: none !important;
}
</style>
